import { BaseQueryFn, FetchArgs, FetchBaseQueryError, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { setLastActiveSession, setLogout, setTokens } from "features/authSlice";
import { TokensRequest, authenticationApi } from "pages/authentication/authenticationService";
import { dashboardLogoutApi } from "pages/dashboard/home/dashboardLogoutService";
import { RootState } from "store";
import * as Constant from "utilities/Constant";
import { minutesToSecondsConvertion, subtractDates } from "utilities/date";
import { logOutApi } from "pages/login/logoutService";
import { deleteAllFiles } from "utilities/files";

export const baseQuery = fetchBaseQuery({ baseUrl: Constant.BASE_BFF_WEB_URL });

const baseQueryWithHeader = fetchBaseQuery({
  baseUrl: Constant.BASE_BFF_WEB_URL,
  prepareHeaders: (headers: Headers, { getState }) => {
    const accessToken = (getState() as RootState).auth.accessToken;
    if (accessToken) {
      headers.set("authorization", `Bearer ${accessToken}`);
    }
    return headers;
  },
});
let didLogOut = false;
let refreshPromise: Promise<any> | null = null;
export const baseQueryWithAuth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (args, api, extraOptions) => {
  const { refreshToken, lastActiveTime, tokenExpiration } = (api.getState() as RootState).auth.tokenValidationParams;
  const accessToken = (api.getState() as RootState).auth.accessToken;
  const currentDate = new Date();
  const isUserActiveSessionLimitExceeds =
    subtractDates(currentDate, lastActiveTime) > minutesToSecondsConvertion(Constant.ACCESS_TOKEN_EXPIRATION_MINUTES);
  const isTokenExpired = subtractDates(tokenExpiration, currentDate) < minutesToSecondsConvertion(Constant.SKEW_TIME_IN_MINUTES);
  const dashboardSessionId = sessionStorage.getItem(Constant.DASHBOARD_SESSION_ID);
  const userId = (api.getState() as RootState).auth.userId;

  if (isUserActiveSessionLimitExceeds) {
    if (!didLogOut) {
      didLogOut = true;
      await deleteAllFiles();
      await api.dispatch(logOutApi.endpoints.postSessionInactvityLogout.initiate(userId));
      if (userId && dashboardSessionId) {
        await api
          .dispatch(
            dashboardLogoutApi.endpoints.updateDashboardSessionEnd.initiate({
              userId: userId,
              dashboardSession: {
                dashboardSessionId: sessionStorage.getItem(Constant.DASHBOARD_SESSION_ID) ?? "",
                dashboardSessionStartTimestamp: sessionStorage.getItem(Constant.DASHBOARD_SESSION_START_TIMESTAMP),
                dashboardSessionEndTimestamp: new Date().toISOString(),
              },
            })
          )
          .unwrap();
      }
      sessionStorage.clear();
      api.dispatch(setLogout());
    }
  } else if (isTokenExpired) {
    const tokenRequestData: TokensRequest = {
      accessToken: accessToken,
      refreshToken: refreshToken,
    };

    if (!refreshPromise && tokenRequestData.accessToken) {
      refreshPromise = api
        .dispatch(authenticationApi.endpoints.getNewTokens.initiate(tokenRequestData))
        .unwrap()
        .then((data) => api.dispatch(setTokens(data)))
        .catch(() => api.dispatch(setLogout()))
        .finally(() => refreshPromise = null);
    }
    await refreshPromise;
  }
  const result = await baseQueryWithHeader(args, api, extraOptions);
  if (result.error?.status == 401 || result.error?.status == 403) {
    api.dispatch(setLogout());
  }
  api.dispatch(setLastActiveSession(new Date()));
  return result;
};
